/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-unused-vars, no-nested-ternary */
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { CornerDownRight, BugPlay, Bug } from 'lucide-react';
import { useDataProvider, useNotify } from 'react-admin';
import { logger } from '@musedrops/shared';
import type { GenModelPreset } from '@musedrops/shared';
import { testModelPreset } from '../../../services/presetTestApi';

interface ModelPresetsEditorProps {
  preset: GenModelPreset | null;
  isCreating: boolean;
  onSave: (preset: GenModelPreset) => Promise<void>;
  onChange: () => void;
  hasUnsavedChanges: boolean;
}

// Form data type with serialized fields for editing
type ModelPresetFormData = Omit<GenModelPreset, 'headers' | 'body'> & {
  headers: string;
  body: string;
  responsePaths: string;
};

export const ModelPresetsEditor: React.FC<ModelPresetsEditorProps> = ({
  preset,
  isCreating,
  onSave,
  onChange,
  hasUnsavedChanges,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const testResultRef = React.useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<Partial<ModelPresetFormData>>({
    type: 'text',
    name: '',
    key: '',
    endpoint: '',
    headers: '',
    body: '',
    responsePaths: '',
    isDebug: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    content: string;
  } | null>(null);
  const [usedBy, setUsedBy] = useState<string[]>([]);
  const [existingDebugPreset, setExistingDebugPreset] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Check for existing debug preset of the same type
  const checkExistingDebugPreset = async (type: string, excludeId?: string) => {
    try {
      const { data } = await dataProvider.getList('gen_model_presets', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'name', order: 'ASC' },
        filter: { type, is_debug: true },
      });
      const existing = data.find((p: GenModelPreset) => p.id !== excludeId);
      if (existing) {
        setExistingDebugPreset({ id: existing.id, name: existing.name });
      } else {
        setExistingDebugPreset(null);
      }
    } catch (error) {
      logger.error('Failed to check existing debug preset:', error);
      setExistingDebugPreset(null);
    }
  };

  // Load preset data when selected
  useEffect(() => {
    if (preset) {
      // Ensure body and headers are strings for the form
      const presetData = {
        ...preset,
        headers:
          typeof preset.headers === 'string'
            ? preset.headers
            : JSON.stringify(preset.headers || {}, null, 2),
        body:
          typeof preset.body === 'string'
            ? preset.body
            : JSON.stringify(preset.body, null, 2),
        responsePaths: preset.responsePaths || '',
        isDebug: preset.isDebug || false,
        inputSampleRate: preset.inputSampleRate ?? null,
        outputAudioFormat: preset.outputAudioFormat ?? null,
        voiceSet: preset.voiceSet ?? null,
        pricePerMInput: preset.pricePerMInput ?? null,
        pricePerMOutput: preset.pricePerMOutput ?? null,
        pricePerKGrounding: preset.pricePerKGrounding ?? null,
      };
      setFormData(presetData);
      if (!isCreating && preset.key) {
        loadUsageInfo(preset.key);
      }
      // Check for existing debug preset of this type (excluding current preset)
      checkExistingDebugPreset(preset.type, preset.id);
    } else if (isCreating) {
      setFormData({
        type: 'text',
        name: '',
        key: '',
        endpoint: '',
        headers: '',
        body: '',
        responsePaths: '',
        isDebug: false,
        inputSampleRate: null,
        outputAudioFormat: null,
        voiceSet: null,
        pricePerMInput: null,
        pricePerMOutput: null,
        pricePerKGrounding: null,
      });
      setUsedBy([]);
      // Check for existing debug preset of default type
      checkExistingDebugPreset('text');
    }
    setErrors({}); // Clear errors when loading new data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, isCreating]);

  const loadUsageInfo = async (presetKey: string) => {
    try {
      // Query gen_prompts table to find usage
      const { data } = await dataProvider.getList('gen_prompts', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'key', order: 'ASC' },
        filter: { model_preset: presetKey },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setUsedBy(data.map((prompt: any) => prompt.key));
    } catch (error) {
      logger.error('Failed to load usage info:', error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFieldChange = (field: keyof GenModelPreset, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    onChange();

    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: '' }));

    // Re-check existing debug preset when type changes
    if (field === 'type') {
      checkExistingDebugPreset(value, preset?.id);
    }
  };

  const generateKey = () => {
    if (formData.name) {
      const key = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      handleFieldChange('key', key);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.key?.trim()) {
      newErrors.key = 'Key is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.key)) {
      newErrors.key =
        'Key must be lowercase letters, numbers, and hyphens only';
    }
    if (!formData.endpoint?.trim()) {
      newErrors.endpoint = 'Endpoint is required';
    } else {
      try {
        // Validate it's a URL
        const url = new URL(formData.endpoint);
        // Require HTTPS for security
        if (url.protocol !== 'https:') {
          newErrors.endpoint = 'Must use HTTPS';
        }
      } catch {
        newErrors.endpoint = 'Must be a valid URL';
      }
    }

    // Validate headers JSON if provided
    const headersString =
      typeof formData.headers === 'string'
        ? formData.headers
        : JSON.stringify(formData.headers || {});
    if (headersString.trim()) {
      try {
        JSON.parse(headersString);
      } catch {
        newErrors.headers = 'Invalid JSON format';
      }
    }
    const bodyString =
      typeof formData.body === 'string'
        ? formData.body
        : JSON.stringify(formData.body || {});
    if (!bodyString.trim()) {
      if (formData.type === 'voice') {
        newErrors.body =
          'Body is required and must contain {{text}} (or {{turns}}), {{voice1}}, and optionally {{instructions}} placeholders';
      } else {
        newErrors.body =
          'Body is required and must contain {{prompt}} placeholder';
      }
    } else {
      try {
        JSON.parse(bodyString);
        // Check for appropriate placeholders based on type
        if (formData.type === 'voice') {
          // Voice presets must have {{text}} (flat script) or {{turns}}
          // (compiled per-turn array — substituted only as a whole JSON
          // value, so it must appear as exactly "{{turns}}") and {{voice1}}
          if (
            !bodyString.includes('{{text}}') &&
            !bodyString.includes('"{{turns}}"')
          ) {
            newErrors.body =
              'Body must contain {{text}} or a whole-value "{{turns}}" placeholder for voice presets';
          } else if (!bodyString.includes('{{voice1}}')) {
            newErrors.body =
              'Body must contain {{voice1}} placeholder for voice presets';
          }
          // {{instructions}} is optional
        } else if (!bodyString.includes('{{prompt}}')) {
          // Text presets must have {{prompt}} placeholder
          newErrors.body = 'Body must contain {{prompt}} placeholder';
        }
      } catch {
        newErrors.body = 'Invalid JSON format';
      }
    }
    // responsePaths is optional for voice presets (binary responses), required for others
    if (formData.type !== 'voice' && !formData.responsePaths?.trim()) {
      newErrors.responsePaths = 'Response paths is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      // If setting this preset as debug, first unset any existing debug preset of the same type
      if (formData.isDebug && existingDebugPreset) {
        try {
          await dataProvider.update('gen_model_presets', {
            id: existingDebugPreset.id,
            data: { is_debug: false },
            previousData: { id: existingDebugPreset.id },
          });
          logger.info(
            `Unset previous debug preset: ${existingDebugPreset.name}`
          );
        } catch (error) {
          logger.error('Failed to unset previous debug preset:', error);
          notify('Failed to update previous debug preset', { type: 'error' });
          setSaving(false);
          return;
        }
      }

      // Ensure body and headers are valid JSON objects for the database (JSONB fields)
      const bodyString =
        typeof formData.body === 'string'
          ? formData.body
          : JSON.stringify(formData.body || {});
      const headersString =
        typeof formData.headers === 'string'
          ? formData.headers
          : JSON.stringify(formData.headers || {});

      // Parse the strings to JSON objects for database storage
      let bodyObject: any;
      let headersObject: any;
      try {
        bodyObject = bodyString.trim() ? JSON.parse(bodyString) : {};
        headersObject = headersString.trim() ? JSON.parse(headersString) : {};
      } catch {
        // This shouldn't happen as we validate JSON in validateForm, but just in case
        notify('Invalid JSON in body or headers field', { type: 'error' });
        setSaving(false);
        return;
      }

      // Ensure endpoint has a path (database constraint requires it)
      let endpoint = formData.endpoint || '';
      if (endpoint && !endpoint.includes('/', 8)) {
        // 8 to skip https://
        endpoint += '/';
      }

      const dataToSave = {
        ...formData,
        endpoint,
        headers: headersObject, // Store as object for JSONB field
        body: bodyObject, // Store as object for JSONB field
        responsePaths: formData.responsePaths || null, // Ensure nullable
        isDebug: formData.isDebug || false, // Explicitly include isDebug
      } as GenModelPreset;

      await onSave(dataToSave);
      notify('Preset saved successfully', { type: 'success' });
      setErrors({});

      // Clear the existing debug preset state since we just saved
      if (formData.isDebug) {
        setExistingDebugPreset(null);
      }
    } catch {
      notify('Failed to save preset', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (formData.type !== 'text' && formData.type !== 'image') {
      notify('Testing is currently only available for text and image presets', {
        type: 'info',
      });
      return;
    }

    if (hasUnsavedChanges) {
      notify('Please save changes before testing', { type: 'warning' });
      return;
    }

    if (!preset?.key) {
      notify('Please save the preset before testing', { type: 'warning' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    // Call the test API with a prompt that returns valid JSON
    const testPrompt =
      'Say hi in json and nothing else.\n\nOUTPUT FORMAT:\n{"response":"Hi"}';

    try {
      const result = await testModelPreset(preset.key, testPrompt);

      // Extract the inner result - result is a nested StandardResponse
      // Structure: { result: { success: bool, result: actual_content, error?: string }, success: true }
      let content: string;
      let success: boolean;

      if (result && typeof result === 'object' && 'result' in result) {
        const innerResult = result.result;
        if (
          innerResult &&
          typeof innerResult === 'object' &&
          'success' in innerResult
        ) {
          success = innerResult.success;
          if (success && innerResult.result) {
            content =
              typeof innerResult.result === 'string'
                ? innerResult.result
                : JSON.stringify(innerResult.result, null, 2);
          } else {
            content = innerResult.error || 'Test failed';
          }
        } else {
          // Fallback if not nested
          content =
            typeof innerResult === 'string'
              ? innerResult
              : JSON.stringify(innerResult, null, 2);
          success = true;
        }
      } else {
        content =
          typeof result === 'string' ? result : JSON.stringify(result, null, 2);
        success = true;
      }

      setTestResult({ success, content });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setTestResult({ success: false, content: errorMessage });
    } finally {
      setTesting(false);

      // Scroll to show the result
      setTimeout(() => {
        testResultRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">
          {isCreating ? 'Create Preset' : 'Edit Preset'}
        </Typography>
      </Box>

      {/* Form */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Warning for presets in use */}
          {!isCreating && usedBy.length > 0 && (
            <Alert severity="warning">Used by {usedBy.join(', ')}</Alert>
          )}

          {/* Type selector and Debug toggle */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ width: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type || 'text'}
                label="Type"
                onChange={e => handleFieldChange('type', e.target.value)}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="voice">Voice</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="music">Music</MenuItem>
              </Select>
            </FormControl>

            {/* Debug preset toggle */}
            <Tooltip
              title={
                existingDebugPreset && !formData.isDebug
                  ? `"${existingDebugPreset.name}" is currently the debug preset for ${formData.type}. Enabling this will replace it.`
                  : formData.isDebug
                    ? 'This preset will be used when debug mode is enabled'
                    : 'Mark this as the debug preset for this type'
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDebug || false}
                    onChange={e =>
                      handleFieldChange('isDebug', e.target.checked)
                    }
                    color="error"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Bug
                      size={16}
                      color={formData.isDebug ? '#f44336' : '#888'}
                    />
                    <Typography
                      variant="body2"
                      color={formData.isDebug ? 'error' : 'text.secondary'}
                    >
                      Debug Preset
                    </Typography>
                  </Box>
                }
              />
            </Tooltip>
          </Box>

          {/* Warning about replacing existing debug preset */}
          {existingDebugPreset && formData.isDebug && (
            <Alert severity="warning" sx={{ mt: -2 }}>
              Saving will replace &ldquo;{existingDebugPreset.name}&rdquo; as
              the debug preset for {formData.type} type.
            </Alert>
          )}

          {/* Input Sample Rate - only for voice presets */}
          {formData.type === 'voice' && (
            <FormControl sx={{ width: 200 }}>
              <InputLabel>Input Sample Rate</InputLabel>
              <Select
                value={formData.inputSampleRate ?? ''}
                label="Input Sample Rate"
                onChange={e =>
                  handleFieldChange(
                    'inputSampleRate',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">
                  <em>Not set</em>
                </MenuItem>
                <MenuItem value={24000}>24000 Hz (Gemini TTS)</MenuItem>
                <MenuItem value={48000}>48000 Hz (Google Cloud TTS)</MenuItem>
                <MenuItem value={44100}>44100 Hz</MenuItem>
                <MenuItem value={22050}>22050 Hz</MenuItem>
                <MenuItem value={16000}>16000 Hz</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Output Audio Format - only for voice presets */}
          {formData.type === 'voice' && (
            <FormControl sx={{ width: 200 }}>
              <InputLabel>Output Audio Format</InputLabel>
              <Select
                value={formData.outputAudioFormat ?? ''}
                label="Output Audio Format"
                onChange={e =>
                  handleFieldChange(
                    'outputAudioFormat',
                    e.target.value === '' ? null : e.target.value
                  )
                }
              >
                <MenuItem value="">
                  <em>Auto-detect</em>
                </MenuItem>
                <MenuItem value="raw-pcm">Raw PCM (Gemini TTS)</MenuItem>
                <MenuItem value="mp3">MP3</MenuItem>
                <MenuItem value="wav">WAV</MenuItem>
                <MenuItem value="ogg">OGG</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Voice set - only for voice presets */}
          {formData.type === 'voice' && (
            <TextField
              label="Voice Set"
              value={formData.voiceSet ?? ''}
              onChange={e =>
                handleFieldChange(
                  'voiceSet',
                  e.target.value.trim() === '' ? null : e.target.value.trim()
                )
              }
              helperText="Column of the voice-profile mappings this preset uses (e.g. gemini). Empty = this preset's own key."
              sx={{ width: 320 }}
            />
          )}

          {/* Name field */}
          <TextField
            label="Preset Name"
            value={formData.name || ''}
            onChange={e => handleFieldChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />

          {/* Key field with auto-generate button */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <IconButton
              onClick={generateKey}
              disabled={!formData.name}
              sx={{ mt: 1 }}
              title="Generate key from name"
            >
              <CornerDownRight />
            </IconButton>
            <TextField
              label="Key"
              value={formData.key || ''}
              onChange={e => handleFieldChange('key', e.target.value)}
              error={!!errors.key}
              helperText={errors.key}
              required
              fullWidth
            />
          </Box>

          {/* Endpoint field */}
          <TextField
            label="Endpoint"
            value={formData.endpoint || ''}
            onChange={e => handleFieldChange('endpoint', e.target.value)}
            error={!!errors.endpoint}
            helperText={errors.endpoint}
            required
            fullWidth
          />

          {/* Headers field - JSON textarea */}
          <TextField
            label="Headers"
            value={
              typeof formData.headers === 'string'
                ? formData.headers
                : JSON.stringify(formData.headers || {}, null, 2)
            }
            onChange={e => handleFieldChange('headers', e.target.value)}
            error={!!errors.headers}
            helperText={
              errors.headers ||
              'JSON headers object with template variables. Example: {"Content-Type": "application/json", "Authorization": "Bearer {{API_KEY}}"}'
            }
            placeholder='{"Content-Type": "application/json", "Authorization": "Bearer {{API_KEY}}"}'
            multiline
            rows={4}
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />

          {/* Body field - JSON textarea */}
          <TextField
            label="Body"
            value={
              typeof formData.body === 'string'
                ? formData.body
                : JSON.stringify(formData.body || {}, null, 2)
            }
            onChange={e => handleFieldChange('body', e.target.value)}
            error={!!errors.body}
            helperText={
              errors.body ||
              (formData.type === 'voice'
                ? 'JSON request body. Must include {{voice1}} and either {{text}} (flat script) or {{turns}} (script compiled to per-turn items, e.g. Gemini 3.8). {{instructions}} is optional. Example: {"model": "tts-1", "voice": "{{voice1}}", "input": "{{text}}"}'
                : 'JSON request body. Must include {{prompt}} placeholder. Example: {"model": "gpt-4", "messages": [{"role": "user", "content": "{{prompt}}"}]}')
            }
            placeholder={
              formData.type === 'voice'
                ? '{"model": "tts-1", "voice": "{{voice1}}", "input": "{{text}}"}'
                : '{"model": "gpt-4", "messages": [{"role": "user", "content": "{{prompt}}"}]}'
            }
            required
            multiline
            rows={8}
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />

          {/* Response paths field with test button */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Response Paths"
                value={formData.responsePaths || ''}
                onChange={e =>
                  handleFieldChange('responsePaths', e.target.value)
                }
                error={!!errors.responsePaths}
                helperText={
                  errors.responsePaths ||
                  (formData.type === 'voice'
                    ? 'Optional for voice presets. E.g: candidates[0].content.parts[0].inlineData.data (for base64), url (for URLs). Comma separate for multiple.'
                    : formData.type === 'image'
                      ? 'E.g: data[0].url, images[0].url, parts.find(inlineData).inlineData.data. Supports: array[0], find(prop=value), find(prop). Comma separate for multiple.'
                      : 'E.g: output.find(type=message).parts.0.text, parts.find(text).text. Supports: array[0], find(prop=value), find(prop). Comma separate for multiple.')
                }
                required={formData.type !== 'voice'}
                fullWidth
                sx={{ flex: 1 }}
              />
              {/* Test button - show for text and image presets */}
              {(formData.type === 'text' || formData.type === 'image') && (
                <IconButton
                  onClick={handleTest}
                  disabled={testing}
                  title="Test preset (uses current form values)"
                  sx={{ mt: 1 }}
                >
                  {testing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <BugPlay size={20} />
                  )}
                </IconButton>
              )}
            </Box>

            {/* Test result display */}
            {testResult && (
              <Box ref={testResultRef}>
                <Alert
                  severity={testResult.success ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                  onClose={() => setTestResult(null)}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                  >
                    {testResult.content}
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>

          {/* Pricing fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Input $/M"
              type="number"
              value={formData.pricePerMInput ?? ''}
              onChange={e =>
                handleFieldChange(
                  'pricePerMInput',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              inputProps={{ step: 'any', min: 0 }}
              fullWidth
            />
            <TextField
              label="Output $/M"
              type="number"
              value={formData.pricePerMOutput ?? ''}
              onChange={e =>
                handleFieldChange(
                  'pricePerMOutput',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              inputProps={{ step: 'any', min: 0 }}
              fullWidth
            />
            <TextField
              label="Ground $/K"
              type="number"
              value={formData.pricePerKGrounding ?? ''}
              onChange={e =>
                handleFieldChange(
                  'pricePerKGrounding',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              inputProps={{ step: 'any', min: 0 }}
              fullWidth
            />
          </Box>
        </Stack>
      </Box>

      {/* Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
        {hasUnsavedChanges && (
          <Typography
            variant="body2"
            color="warning.main"
            sx={{ ml: 'auto', alignSelf: 'center' }}
          >
            Unsaved changes
          </Typography>
        )}
      </Box>
    </Box>
  );
};
